import { addDays, format } from 'date-fns'
import { currentWeekStart } from './homework-data'

export type ReadinessTarget =
  | 'general'
  | 'google'
  | 'meta'
  | 'apple'
  | 'amazon'
  | 'netflix'
  | 'openai'
  | 'anthropic'

export const READINESS_TARGETS: { value: ReadinessTarget; label: string }[] = [
  { value: 'general', label: 'General interview prep' },
  { value: 'google', label: 'Google' },
  { value: 'meta', label: 'Meta' },
  { value: 'apple', label: 'Apple' },
  { value: 'amazon', label: 'Amazon' },
  { value: 'netflix', label: 'Netflix' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
]

export type ReadinessBand =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green-light'
  | 'green-dark'

export interface ReadinessBandInfo {
  band: ReadinessBand
  label: string
  textClass: string
  bgClass: string
}

const BANDS: { min: number; info: ReadinessBandInfo }[] = [
  {
    min: 85,
    info: {
      band: 'green-dark',
      label: 'Interview ready',
      textClass: 'text-ready-green-dark',
      bgClass: 'bg-ready-green-dark',
    },
  },
  {
    min: 70,
    info: {
      band: 'green-light',
      label: 'Nearly ready',
      textClass: 'text-ready-green-light',
      bgClass: 'bg-ready-green-light',
    },
  },
  {
    min: 55,
    info: {
      band: 'yellow',
      label: 'On track',
      textClass: 'text-ready-yellow',
      bgClass: 'bg-ready-yellow',
    },
  },
  {
    min: 40,
    info: {
      band: 'orange',
      label: 'Building up',
      textClass: 'text-ready-orange',
      bgClass: 'bg-ready-orange',
    },
  },
  {
    min: 0,
    info: {
      band: 'red',
      label: 'Not ready yet',
      textClass: 'text-ready-red',
      bgClass: 'bg-ready-red',
    },
  },
]

export function readinessBand(score: number): ReadinessBandInfo {
  return (BANDS.find((b) => score >= b.min) ?? BANDS[BANDS.length - 1]).info
}

export interface SprintScore {
  sprintNumber: number
  startDate: string
  weekLabel: string
  score: number
}

const CURRENT_SPRINT_NUMBER = 14
const FIRST_SCORED_SPRINT = 6

export type Pillar = 'coding' | 'systemDesign' | 'behavioral'

export const PILLARS: { value: Pillar; label: string }[] = [
  { value: 'coding', label: 'Coding' },
  { value: 'systemDesign', label: 'System design' },
  { value: 'behavioral', label: 'Behavioral' },
]

// Raw ability per pillar, sprint 6 → 14 (current), oldest first.
const RAW_PILLAR_HISTORY: Record<Pillar, number[]> = {
  coding: [30, 34, 38, 43, 42, 51, 56, 62, 70],
  systemDesign: [22, 25, 29, 33, 34, 40, 46, 52, 57],
  behavioral: [45, 48, 52, 55, 54, 60, 65, 70, 76],
}

interface TargetProfile {
  bar: Record<Pillar, number>
  weight: Record<Pillar, number>
}

const TARGET_PROFILES: Record<ReadinessTarget, TargetProfile> = {
  general: {
    bar: { coding: 88, systemDesign: 85, behavioral: 88 },
    weight: { coding: 40, systemDesign: 30, behavioral: 30 },
  },
  google: {
    bar: { coding: 96, systemDesign: 62, behavioral: 78 },
    weight: { coding: 55, systemDesign: 15, behavioral: 30 },
  },
  meta: {
    bar: { coding: 82, systemDesign: 78, behavioral: 76 },
    weight: { coding: 40, systemDesign: 35, behavioral: 25 },
  },
  amazon: {
    bar: { coding: 78, systemDesign: 74, behavioral: 92 },
    weight: { coding: 30, systemDesign: 25, behavioral: 45 },
  },
  netflix: {
    bar: { coding: 64, systemDesign: 97, behavioral: 82 },
    weight: { coding: 15, systemDesign: 55, behavioral: 30 },
  },
  openai: {
    bar: { coding: 94, systemDesign: 90, behavioral: 72 },
    weight: { coding: 45, systemDesign: 40, behavioral: 15 },
  },
  apple: {
    bar: { coding: 86, systemDesign: 82, behavioral: 74 },
    weight: { coding: 45, systemDesign: 30, behavioral: 25 },
  },
  anthropic: {
    bar: { coding: 92, systemDesign: 86, behavioral: 80 },
    weight: { coding: 40, systemDesign: 35, behavioral: 25 },
  },
}

const MAX_SCORE = 97

function pillarReadinessAt(
  target: ReadinessTarget,
  pillar: Pillar,
  sprintIndex: number,
): number {
  const raw = RAW_PILLAR_HISTORY[pillar][sprintIndex]
  const bar = TARGET_PROFILES[target].bar[pillar]
  return Math.min(MAX_SCORE, Math.round((raw / bar) * 100))
}

function overallScoreAt(target: ReadinessTarget, sprintIndex: number): number {
  const { weight } = TARGET_PROFILES[target]
  const total = weight.coding + weight.systemDesign + weight.behavioral
  const sum = PILLARS.reduce(
    (acc, p) =>
      acc + pillarReadinessAt(target, p.value, sprintIndex) * weight[p.value],
    0,
  )
  return Math.min(MAX_SCORE, Math.round(sum / total))
}

const SPRINT_COUNT = RAW_PILLAR_HISTORY.coding.length

/** Full per-sprint overall score history for a target, oldest first. */
export function getReadinessHistory(target: ReadinessTarget): SprintScore[] {
  const thisMonday = currentWeekStart()

  return Array.from({ length: SPRINT_COUNT }, (_, i) => {
    const sprintNumber = FIRST_SCORED_SPRINT + i
    const start = addDays(
      thisMonday,
      (sprintNumber - CURRENT_SPRINT_NUMBER) * 7,
    )
    return {
      sprintNumber,
      startDate: format(start, 'yyyy-MM-dd'),
      weekLabel: format(start, 'MMM d'),
      score: overallScoreAt(target, i),
    }
  })
}

export type Trend = 'up' | 'down' | 'flat'

export interface SkillDetail {
  name: string
  score: number
  delta: number
  trend: Trend
}

export interface PillarDetail {
  pillar: Pillar
  label: string
  score: number
  delta: number
  trend: Trend
  weight: number
  skills: SkillDetail[]
}

const SKILL_HISTORY: Record<
  Pillar,
  { name: string; prev: number; now: number }[]
> = {
  coding: [
    { name: 'Arrays & strings', prev: 74, now: 81 },
    { name: 'Dynamic programming', prev: 44, now: 47 },
    { name: 'Graphs & trees', prev: 58, now: 66 },
    { name: 'Time & space analysis', prev: 68, now: 68 },
  ],
  systemDesign: [
    { name: 'Scalability & caching', prev: 55, now: 62 },
    { name: 'Data modeling', prev: 49, now: 55 },
    { name: 'API & service design', prev: 52, now: 51 },
    { name: 'Trade-off communication', prev: 43, now: 44 },
  ],
  behavioral: [
    { name: 'STAR storytelling', prev: 72, now: 79 },
    { name: 'Leadership examples', prev: 61, now: 67 },
    { name: 'Conflict & failure stories', prev: 65, now: 64 },
  ],
}

const FLAT_THRESHOLD = 1

function trendOf(delta: number): Trend {
  if (delta > FLAT_THRESHOLD) return 'up'
  if (delta < -FLAT_THRESHOLD) return 'down'
  return 'flat'
}

function scaleToBar(raw: number, bar: number): number {
  return Math.min(MAX_SCORE, Math.round((raw / bar) * 100))
}

/** Per-pillar breakdown (with sub-skills) for the current sprint vs last. */
export function getReadinessBreakdown(target: ReadinessTarget): PillarDetail[] {
  const profile = TARGET_PROFILES[target]
  const cur = SPRINT_COUNT - 1
  const prev = SPRINT_COUNT - 2
  const totalWeight =
    profile.weight.coding +
    profile.weight.systemDesign +
    profile.weight.behavioral

  return PILLARS.map(({ value: pillar, label }) => {
    const score = pillarReadinessAt(target, pillar, cur)
    const delta = score - pillarReadinessAt(target, pillar, prev)
    const bar = profile.bar[pillar]

    const skills: SkillDetail[] = SKILL_HISTORY[pillar].map((sk) => {
      const now = scaleToBar(sk.now, bar)
      const before = scaleToBar(sk.prev, bar)
      const d = now - before
      return { name: sk.name, score: now, delta: d, trend: trendOf(d) }
    })

    return {
      pillar,
      label,
      score,
      delta,
      trend: trendOf(delta),
      weight: Math.round((profile.weight[pillar] / totalWeight) * 100),
      skills,
    }
  })
}
